import React, {Component} from 'react';
import { Link } from 'react-router-dom';

class ProductItem extends Component {
    getProduct() {
        return this.props.product_details || {};
    }

    getProductImage(product) {
        return product.display_image || product.product_image || product.image_path || '/assets/img/byos-no-image.png';
    }

    getProductLink(product) {
        const variationId = product.variation_id || product.product_id || product.id || '';
        const name = product.product_name || 'Product';
        return `/details/${Buffer.from(String(variationId)).toString('base64')}/${name}`;
    }

    getCardCopy(product) {
        const title = (product.product_name || 'Product').trim();
        const subtitle = (
            product.product_subtitle ||
            product.short_description ||
            product.category_name ||
            product.brand_name ||
            product.tagline ||
            ''
        ).trim();

        return {title, subtitle};
    }

    getPrice(product) {
        const pricing = product.pricing || {};
        return {
            sale: pricing.offer_price || product.offer_price || '',
            regular: pricing.selling_price || product.selling_price || '',
        };
    }

    getRating(product) {
        const value = Number(product.rating || product.rattings || product.average_rating || product.review_rating || 4.8);
        if (Number.isNaN(value)) {
            return 4.8;
        }
        return Math.max(0, Math.min(5, value));
    }

    getReviewCount(product) {
        const count = Number(product.review_count || product.total_reviews || product.total_review || product.reviewCount || 0);
        return Number.isNaN(count) ? 0 : count;
    }

    getIconType(product, title) {
        const text = `${title || ''} ${product.category_name || ''} ${product.brand_name || ''}`.toLowerCase();

        if ((product.icon_type || '').toLowerCase() === 'person') return 'person';
        if ((product.icon_type || '').toLowerCase() === 'lamp') return 'lamp';
        if ((product.icon_type || '').toLowerCase() === 'gift') return 'gift';

        if (text.includes('hair') || text.includes('skin')) return 'person';
        if (text.includes('pooja') || text.includes('lamp') || text.includes('diya')) return 'lamp';
        if (text.includes('combo') || text.includes('gift')) return 'gift';
        return 'bottle';
    }

    renderBadgeIcon(product, title) {
        const iconPath = (product.icon_path || product.icon_url || product.badge_icon_path || '').trim();
        if (iconPath) {
            return <img src={iconPath} alt="" aria-hidden="true" />;
        }

        const iconType = this.getIconType(product, title);

        switch (iconType) {
        case 'person':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M43 20.5c0 6-4.9 10.9-11 10.9S21 26.5 21 20.5 25.9 9.6 32 9.6s11 4.9 11 10.9Z" />
                    <path d="M15 53c2.9-11.1 10.4-16.7 17-16.7S46.1 41.9 49 53" />
                    <path d="M33.5 24.8c-2.8-.4-5.4-2.4-6.8-5.3" />
                    <path d="M37.2 24.8c2.8-.4 5.4-2.4 6.8-5.3" />
                    <path d="M26.7 12.8c1.3 2.2 3.2 3.6 5.3 4.1" />
                </svg>
            );
        case 'lamp':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M32 10c6.2 6.8 10 12.3 10 18.7 0 6.7-4.5 11.3-10 11.3s-10-4.6-10-11.3C22 22.3 25.8 16.8 32 10Z" />
                    <path d="M22 42h20" />
                    <path d="M24 50h16" />
                    <path d="M32 29v20" />
                    <path d="M27 35c2.1 1.8 4.2 2.7 5 2.7s2.9-.9 5-2.7" />
                </svg>
            );
        case 'gift':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M14 26h36v28H14z" />
                    <path d="M32 26v28" />
                    <path d="M14 36h36" />
                    <path d="M32 26c-2.6-7.3-9.3-12-15-12-3.4 0-6 2.5-6 5.7 0 6.1 7.8 11.3 21 11.3" />
                    <path d="M32 26c2.6-7.3 9.3-12 15-12 3.4 0 6 2.5 6 5.7 0 6.1-7.8 11.3-21 11.3" />
                </svg>
            );
        case 'bottle':
        default:
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M26 10h12" />
                    <path d="M28 10v8l-5 6v24c0 5.5 4.5 10 9 10s9-4.5 9-10V24l-5-6v-8" />
                    <path d="M24 26h16" />
                    <path d="M32 30v20" />
                    <path d="M22 48c3.3 2.2 6.7 3.3 10 3.3S38.7 50.2 42 48" />
                </svg>
            );
        }
    }

    renderStars(rating) {
        return Array.from({length: 5}).map((_, index) => {
            const threshold = index + 1;
            const isActive = rating >= threshold;
            const isHalf = !isActive && rating > index && rating < threshold;

            return (
                <span
                    className={`ps-product-item__star ${isActive ? 'ps-product-item__star--active' : ''} ${isHalf ? 'ps-product-item__star--half' : ''}`}
                    key={`star-${index}`}
                    aria-hidden="true"
                >
                    <i className={`fa ${isHalf ? 'fa-star-half-o' : 'fa-star'}`}></i>
                </span>
            );
        });
    }

    render() {
        const product = this.getProduct();
        const {title, subtitle} = this.getCardCopy(product);
        const image = this.getProductImage(product);
        const link = this.getProductLink(product);
        const {sale, regular} = this.getPrice(product);
        const rating = this.getRating(product);
        const reviewCount = this.getReviewCount(product);

        return (
            <article className="ps-product-item">
                <Link className="ps-product-item__link" to={link} aria-label={title}>
                    <div className="ps-product-item__media">
                        <img className="ps-product-item__image" src={image} alt={title} loading="lazy" />
                    </div>

                    <div className="ps-product-item__badge" aria-hidden="true">
                        <span className="ps-product-item__badgeCircle">
                            {this.renderBadgeIcon(product, title)}
                        </span>
                    </div>

                    <div className="ps-product-item__content">
                        <h5 className="ps-product-item__title">{title}</h5>
                        {subtitle ? <p className="ps-product-item__subtitle">{subtitle}</p> : <p className="ps-product-item__subtitle ps-product-item__subtitle--empty">&nbsp;</p>}

                        <div className="ps-product-item__rating" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
                            <div className="ps-product-item__stars">{this.renderStars(rating)}</div>
                            <span className="ps-product-item__reviewCount">({reviewCount})</span>
                        </div>

                        <div className="ps-product-item__priceRow">
                            {sale ? <span className="ps-product-item__price">₹{sale}</span> : null}
                            {regular ? <span className="ps-product-item__strike">₹{regular}</span> : null}
                        </div>

                        <span className="ps-product-item__button">
                            Shop Now
                            <img src="/assets/img/right-arrow-small.png" alt="" aria-hidden="true" />
                        </span>
                    </div>
                </Link>
            </article>
        );
    }
}

export default ProductItem;
